import { useState } from 'react'
import type { WizardState, Company } from './types'
import { SectionBlock, FieldRow, RadioPillGrid, Expandable, inputStyle, BLUE } from './ui'
import { apiFetch } from '../../../lib/api-client'

const UDLEJER_TYPE_OPTIONS = [
  { label: 'Privatperson', value: 'person' },
  { label: 'Virksomhed', value: 'virksomhed' },
]

const COUNTRY_CODES = [
  { flag: '🇩🇰', code: '+45', label: 'Danmark' },
  { flag: '🇸🇪', code: '+46', label: 'Sverige' },
  { flag: '🇳🇴', code: '+47', label: 'Norge' },
  { flag: '🇩🇪', code: '+49', label: 'Tyskland' },
  { flag: '🇬🇧', code: '+44', label: 'UK' },
]

const IconBuilding = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="1"/>
    <path d="M9 3v18M15 3v18M3 9h18M3 15h18"/>
  </svg>
)

const IconCreditCard = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="1" y="4" width="22" height="16" rx="2"/>
    <path d="M1 10h22"/>
  </svg>
)

interface Props {
  state: WizardState
  onChange: (p: Partial<WizardState>) => void
  company: Company | null
  onCompanyUpdate: (patch: Partial<Company>) => void
}

export function SectionUdlejer({ state, onChange, company, onCompanyUpdate }: Props) {
  const [countryCode, setCountryCode] = useState('+45')
  const [cvrInput, setCvrInput] = useState(company?.cvr ?? '')
  const [cvrLoading, setCvrLoading] = useState(false)
  const [cvrError, setCvrError] = useState('')

  const name = company?.name ?? ''
  const address = company?.address ?? ''
  const email = company?.email ?? ''
  const phone = company?.phone ?? ''
  const selectedCountry = COUNTRY_CODES.find(c => c.code === countryCode) ?? COUNTRY_CODES[0]

  const betalingMethod = state.payment_method || 'manuel'

  async function handleCVRFetch() {
    const cvr = cvrInput.replace(/\s/g, '')
    if (!cvr || cvr.length !== 8) {
      setCvrError('Indtast et gyldigt 8-cifret CVR-nummer')
      return
    }
    setCvrLoading(true)
    setCvrError('')
    try {
      const data = await apiFetch<{
        name: string
        address: string
        phone: string
        email: string
        cvr: string
      }>(`/api/v1/companies/cvr-lookup/${cvr}`)
      onCompanyUpdate({
        name: data.name || company?.name,
        address: data.address || company?.address,
        phone: data.phone || company?.phone,
        email: data.email || company?.email,
        cvr: data.cvr,
      })
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'CVR-opslag fejlede'
      setCvrError(msg)
    } finally {
      setCvrLoading(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* ── Udlejer ──────────────────────────────────────────────────────── */}
      <SectionBlock icon={<IconBuilding />} title="Udlejer" subtitle="Hvem udlejer lejemålet?">
        <FieldRow label="Hvilken type udlejer?">
          <RadioPillGrid options={UDLEJER_TYPE_OPTIONS} value="virksomhed" onChange={() => {}} />
        </FieldRow>
        <Expandable open={true}>
          <div style={{ display: 'flex', gap: 12, marginBottom: cvrError ? 8 : 16, alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <FieldRow label="CVR-nummer">
                <input
                  style={inputStyle}
                  value={cvrInput}
                  onChange={e => { setCvrInput(e.target.value); setCvrError('') }}
                  placeholder="44158329"
                  maxLength={8}
                />
              </FieldRow>
            </div>
            <button
              onClick={handleCVRFetch}
              disabled={cvrLoading}
              style={{
                padding: '10px 16px', background: cvrLoading ? '#93c5fd' : BLUE, color: 'white',
                border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600,
                cursor: cvrLoading ? 'not-allowed' : 'pointer', flexShrink: 0, marginBottom: 16,
              }}
            >
              {cvrLoading ? 'Henter…' : 'Hent fra CVR'}
            </button>
          </div>
          {cvrError && (
            <div style={{ marginBottom: 12, fontSize: 12, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '6px 10px' }}>
              {cvrError}
            </div>
          )}
          <FieldRow label="Virksomhedsnavn" required>
            <input
              style={inputStyle}
              value={name}
              onChange={e => onCompanyUpdate({ name: e.target.value })}
              placeholder="TOPAMA Ejendomme"
            />
          </FieldRow>
          <FieldRow label="Adresse">
            <input
              style={inputStyle}
              value={address}
              onChange={e => onCompanyUpdate({ address: e.target.value })}
              placeholder="Silkeborgvej 14A"
            />
          </FieldRow>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 12 }}>
            <FieldRow label="Postnr">
              <input
                style={inputStyle}
                value={company?.postnr ?? ''}
                onChange={e => onCompanyUpdate({ postnr: e.target.value })}
                placeholder="7400"
              />
            </FieldRow>
            <FieldRow label="By">
              <input
                style={inputStyle}
                value={company?.by ?? ''}
                onChange={e => onCompanyUpdate({ by: e.target.value })}
                placeholder="Herning"
              />
            </FieldRow>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <FieldRow label="Kontakt e-mail" required>
              <input
                style={inputStyle}
                value={email}
                onChange={e => onCompanyUpdate({ email: e.target.value })}
                placeholder="kontakt@topama.dk"
              />
            </FieldRow>
            <FieldRow label="Kontakt telefon" required>
              {/* Flag shown as span before the select — emoji in <option> doesn't render on Windows */}
              <div style={{ display: 'flex', gap: 0, borderRadius: 8, overflow: 'hidden', border: '1px solid #d1d5db' }}>
                <div style={{ display: 'flex', alignItems: 'center', background: '#f9fafb', borderRight: '1px solid #d1d5db', flexShrink: 0, position: 'relative' }}>
                  <span style={{ paddingLeft: 10, fontSize: 18, lineHeight: 1, pointerEvents: 'none', userSelect: 'none' }}>
                    {selectedCountry.flag}
                  </span>
                  <select
                    value={countryCode}
                    onChange={e => setCountryCode(e.target.value)}
                    style={{
                      appearance: 'none', border: 'none', background: 'transparent',
                      padding: '9px 26px 9px 6px', fontSize: 13, cursor: 'pointer', color: '#374151',
                      outline: 'none',
                    }}
                  >
                    {COUNTRY_CODES.map(c => (
                      <option key={c.code} value={c.code}>{c.code}</option>
                    ))}
                  </select>
                  <div style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: '#9ca3af', fontSize: 10 }}>▼</div>
                </div>
                <input
                  style={{ ...inputStyle, border: 'none', borderRadius: 0, flex: 1 }}
                  value={phone ?? ''}
                  onChange={e => onCompanyUpdate({ phone: e.target.value })}
                  placeholder={`${selectedCountry.code} 12 34 56 78`}
                />
              </div>
            </FieldRow>
          </div>
        </Expandable>
      </SectionBlock>

      {/* ── Betaling ─────────────────────────────────────────────────────── */}
      <SectionBlock icon={<IconCreditCard />} title="Betaling" subtitle="Hvordan betaler lejeren huslejen?">

        {/* Betalingsservice — disabled (not connected) */}
        <div
          style={{
            padding: '12px 14px', borderRadius: 8,
            border: `1px solid ${betalingMethod === 'betalingsservice' ? BLUE : '#e8eaed'}`,
            background: '#f7f8fa',
            marginBottom: 8, opacity: 0.55, cursor: 'not-allowed',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IconCreditCard />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>Betalingsservice</div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 1 }}>Automatisk opkrævning via Betalingsservice</div>
              </div>
            </div>
            <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, background: '#f3f4f6', color: '#9ca3af', fontWeight: 500 }}>
              Ikke forbundet
            </span>
          </div>
          <div style={{ fontSize: 12, color: '#9ca3af', lineHeight: 1.6 }}>
            Tilslut Betalingsservice under Indstillinger for at aktivere automatisk opkrævning af husleje.
          </div>
        </div>

        {/* Manuel bankoverførsel — selectable */}
        <div
          onClick={() => onChange({ payment_method: 'manuel' })}
          style={{
            padding: '12px 14px', borderRadius: 8, cursor: 'pointer',
            border: `1px solid ${betalingMethod === 'manuel' ? BLUE : '#d1d5db'}`,
            background: betalingMethod === 'manuel' ? '#eef2ff' : 'white',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: 8, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <IconCreditCard />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: betalingMethod === 'manuel' ? BLUE : '#374151' }}>
                  Manuel bankoverførsel
                </div>
                <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 1 }}>Lejeren overfører huslejen til din bankkonto</div>
              </div>
            </div>
            <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, background: '#ecfdf5', color: '#059669', fontWeight: 500 }}>
              Aktiv
            </span>
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.6 }}>
            Betalingsinformation fra din virksomhedsprofil medtages i kontrakten.
          </div>
        </div>

      </SectionBlock>

    </div>
  )
}
